<script>
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import VueApexCharts from "vue3-apexcharts";
import axios from "axios";

export default {
  page: {
    title: "Dashboard Kinerja Pelatihan",
    meta: [{ name: "description", content: appConfig.description }],
  },

  components: {
    Layout,
    PageHeader,
    apexchart: VueApexCharts,
  },

  data() {
    return {
      title: "Dashboard Kinerja Pelatihan",
      items: [
        { text: "Dashboard", href: "/" },
        { text: "Kinerja Pelatihan", active: true },
      ],

      selectedYear: "ALL",
      years: ["ALL", 2022, 2023, 2024],

      /* ================= STATE ================= */
      loading: false,

      kpi: {
        totalPeserta: 0,
        totalPelatihan: 0,
        rataRataPeserta: 0,
        persentaseSelesai: 0,
      },

      pesertaPerKode: {},
      pelatihanPerKode: {},

      showPesertaDetail: false,
      showPelatihanDetail: false,

      activeTab: "ringkasan",

      /* ================= CHART ================= */
      chartPeserta: {
        options: {
          chart: { type: "line", toolbar: { show: false } },
          xaxis: { categories: [] },
          title: { text: "Distribusi Peserta per Tahun" },
        },
        series: [{ name: "Peserta", data: [] }],
      },

      chartPelatihan: {
        options: {
          chart: { type: "line", toolbar: { show: false } },
          xaxis: { categories: [] },
          title: { text: "Distribusi Pelatihan per Tahun" },
        },
        series: [{ name: "Pelatihan", data: [] }],
      },

      chartGender: {
        options: {
          labels: [],
          legend: { position: "bottom" },
          title: { text: "Peserta berdasarkan Gender" },
        },
        series: [],
      },

      chartMetode: {
        options: {
          labels: [],
          legend: { position: "bottom" },
          title: { text: "Distribusi Metode Pembelajaran" },
        },
        series: [],
      },

      chartEvaluasi: {
        options: {
          labels: [],
          legend: { position: "bottom" },
          title: { text: "Status Penyelesaian Pelatihan" },
        },
        series: [],
      },
    };
  },

  watch: {
    selectedYear() {
      this.loadDashboard();
    },
  },

  mounted() {
    this.loadDashboard();
  },

  methods: {
    async loadDashboard() {
      this.loading = true;
      try {
        const token = localStorage.getItem("access_token");

        const res = await axios.get(
          process.env.VUE_APP_BACKEND_URL_VERSION + "dashboard/kinerja-pelatihan",
          {
            params: { year: this.selectedYear },
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const data = res.data?.data;
        if (!data) return;

        /* ===== KPI ===== */
        this.kpi = data.kpi ?? this.kpi;

        /* ===== BREAKDOWN ===== */
        this.pesertaPerKode = data.pesertaPerKode ?? {};
        this.pelatihanPerKode = data.pelatihanPerKode ?? {};

        /* ===== LINE CHART ===== */
        this.chartPeserta.options.xaxis.categories =
          data.chart?.pesertaPerTahun?.categories ?? [];
        this.chartPeserta.series[0].data =
          data.chart?.pesertaPerTahun?.data ?? [];

        this.chartPelatihan.options.xaxis.categories =
          data.chart?.pelatihanPerTahun?.categories ?? [];
        this.chartPelatihan.series[0].data =
          data.chart?.pelatihanPerTahun?.data ?? [];

        /* ===== DONUT ===== */
        this.chartGender.options.labels =
          data.chart?.jenisKelamin?.labels ?? [];
        this.chartGender.series =
          data.chart?.jenisKelamin?.series ?? [];

        this.chartMetode.options.labels =
          data.chart?.metode?.labels ?? [];
        this.chartMetode.series =
          data.chart?.metode?.series ?? [];

        this.chartEvaluasi.options.labels =
          data.chart?.evaluasi?.labels ?? [];
        this.chartEvaluasi.series =
          data.chart?.evaluasi?.series ?? [];

      } catch (err) {
        console.error("Load dashboard failed", err);

        if (err.response?.status === 401) {
          console.warn("Token invalid / expired");
          // optional: redirect login
          // this.$router.push('/login');
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>


<style scoped>
/* ================= KPI ================= */
.kpi-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.25s ease;
  border-left: 6px solid #0d6efd;
}
.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
}
.kpi-value {
  font-size: 30px;
  font-weight: 700;
}
.kpi-label {
  color: #6c757d;
}
.kpi-hint {
  font-size: 12px;
  color: #999;
}

/* ================= BREAKDOWN ================= */
.breakdown-box {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 12px;
  margin-top: 10px;
}
.breakdown-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 14px;
}

/* ================= TAB ================= */
.nav-tabs .nav-link {
  font-weight: 600;
}
.section-title {
  text-align: center;
  font-weight: 600;
  margin-bottom: 10px;
}
</style>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <!-- ================= FILTER ================= -->
    <div class="row mb-3">
      <div class="col-md-3">
        <label class="fw-bold">Filter Tahun</label>
        <select v-model="selectedYear" class="form-select">
          <option v-for="y in years" :key="y" :value="y">
            {{ y }}
          </option>
        </select>
      </div>
    </div>

    <!-- ================= LOADING ================= -->
    <div v-if="loading" class="text-center py-5 text-muted">
      <i class="mdi mdi-loading mdi-spin me-2"></i>
      Memuat dashboard...
    </div>

    <!-- ================= CONTENT ================= -->
    <div v-else>

      <!-- ================= KPI ================= -->
      <div class="row mb-4">
        <div class="col-md-3">
          <div class="kpi-card" @click="showPesertaDetail = !showPesertaDetail">
            <div class="kpi-value">{{ kpi.totalPeserta }}</div>
            <div class="kpi-label">Total Peserta</div>
            <small class="kpi-hint">Klik untuk detail</small>
          </div>

          <div v-if="showPesertaDetail" class="breakdown-box">
            <div
              class="breakdown-item"
              v-for="(v, k) in pesertaPerKode"
              :key="k"
            >
              <span>{{ k }}</span>
              <strong>{{ v }}</strong>
            </div>
          </div>
        </div>

        <div class="col-md-3">
          <div class="kpi-card" @click="showPelatihanDetail = !showPelatihanDetail">
            <div class="kpi-value">{{ kpi.totalPelatihan }}</div>
            <div class="kpi-label">Total Pelatihan</div>
            <small class="kpi-hint">Klik untuk detail</small>
          </div>

          <div v-if="showPelatihanDetail" class="breakdown-box">
            <div
              class="breakdown-item"
              v-for="(v, k) in pelatihanPerKode"
              :key="k"
            >
              <span>{{ k }}</span>
              <strong>{{ v }}</strong>
            </div>
          </div>
        </div>

        <div class="col-md-3">
          <div class="kpi-card">
            <div class="kpi-value">{{ kpi.rataRataPeserta }}</div>
            <div class="kpi-label">Rata-rata Peserta</div>
          </div>
        </div>

        <div class="col-md-3">
          <div class="kpi-card">
            <div class="kpi-value">{{ kpi.persentaseSelesai }}%</div>
            <div class="kpi-label">Pelatihan Selesai</div>
          </div>
        </div>
      </div>

      <!-- ================= LINE CHART ================= -->
      <div class="row mb-4">
        <div class="col-md-6">
          <apexchart
            v-if="chartPeserta.series[0].data.length"
            height="300"
            :options="chartPeserta.options"
            :series="chartPeserta.series"
          />
        </div>

        <div class="col-md-6">
          <apexchart
            v-if="chartPelatihan.series[0].data.length"
            height="300"
            :options="chartPelatihan.options"
            :series="chartPelatihan.series"
          />
        </div>
      </div>

      <!-- ================= DONUT ================= -->
      <div class="row">
        <div class="col-md-4">
          <h6 class="section-title">Peserta berdasarkan Gender</h6>
          <apexchart
            v-if="chartGender.series.length"
            type="donut"
            height="300"
            :options="chartGender.options"
            :series="chartGender.series"
          />
        </div>

        <div class="col-md-4">
          <h6 class="section-title">Metode Pembelajaran</h6>
          <apexchart
            v-if="chartMetode.series.length"
            type="donut"
            height="300"
            :options="chartMetode.options"
            :series="chartMetode.series"
          />
        </div>

        <div class="col-md-4">
          <h6 class="section-title">Status Pelatihan</h6>
          <apexchart
            v-if="chartEvaluasi.series.length"
            type="donut"
            height="300"
            :options="chartEvaluasi.options"
            :series="chartEvaluasi.series"
          />
        </div>
      </div>

    </div>
  </Layout>
</template>

